/* eslint-disable react/jsx-props-no-spreading */
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Category, IssueScopeKind } from '../../../../../shared/issuesV2';
import { window } from '../../../../globals';
import { SecurityRequired } from '../../../generic/Gates';
import IssuesSection from '../../../shared/settings/SettingsPageIssueSection';
import Section from '../../../shared/settings/SettingsPageSection';

import PropagateModal from './PropagateIssuesModal';
import useIssueSettings from './useIssueSettings';
import type { IssueSettingsProps } from './types';

/**
 * SettingsOrganizationProjectIssuesSecurity is the settings page for
 * managing the default security issue settings for projects.
 */
const SettingsOrganizationProjectIssuesSecurity: VoidFunctionComponent<IssueSettingsProps> = ({
  policies: policyOptions,
}) => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const {
    settings,
    isLoading,
    onUpdateSettings,
    policies,
    filters,
    isSaveButtonDisabled,
    onSaveSettings,
  } = useIssueSettings(
    window.user?.organizationId as number,
    Category.Vulnerability,
    policyOptions,
  );

  return (
    <>
      <Section>
        <Section.Left heading="Security">
          <Section.Paragraph>Default configuration for security issues on new projects</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton onClick={() => setIsPropagateModalOpen(true)} isDisabled={!isSaveButtonDisabled} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <IssuesSection
            category={Category.Vulnerability}
            scopeType={IssueScopeKind.Global}
            isScanningEnabled={settings?.isScanningEnabled || false}
            onToggleIsScanningEnabled={(isScanningEnabled) => onUpdateSettings({ isScanningEnabled })}
            isCICheckEnabled={settings?.isCICheckEnabled || false}
            onToggleIsCICheckEnabled={(isCICheckEnabled) => onUpdateSettings({ isCICheckEnabled })}
            policyOptions={policies.policyOptions}
            selectedPolicyOption={policies.selectedPolicyOption}
            onSelectedPolicyChange={(policyId) => onUpdateSettings({ policyId })}
            filterOptions={filters.filterOptions}
            selectedFilterOption={filters.selectedFilterOption}
            onSelectedFilterChange={(filterId) => onUpdateSettings({ filterId })}
            // Users with organization settings permissions can edit the issue policy and filters
            canEditFilters
            canEditPolicy
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagateModal
          category={Category.Vulnerability}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
          policyName={policies.selectedPolicyName}
          filterName={filters.selectedFilterName}
          settings={settings}
        />
      )}
    </>
  );
};

/**
 * Container which gates the Security settings page behind the
 * SecurityRequired component, which checks `organization.security_enabled`
 */
const SettingsOrganizationProjectIssuesSecurityGate: VoidFunctionComponent<IssueSettingsProps> = (props) => (
  <SecurityRequired>
    <SettingsOrganizationProjectIssuesSecurity {...props} />
  </SecurityRequired>
);

export default SettingsOrganizationProjectIssuesSecurityGate;
